import React, { useState, useEffect, useRef } from "react";
import "../styles/ExplorePlaces.css";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { useNavigate } from "react-router-dom";

export default function ExplorePlaces() {
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
  const navigate = useNavigate();

  const interestOptions = [
    "Adventure",
    "Spiritual",
    "Beaches",
    "Cultural",
    "Wildlife",
    "Food",
    "Luxury",
    "Historical",
    "Nature",
    "Hiking",
    "Photography",
    "Road Trips",
    "Camping",
    "Festivals",
    "Nightlife",
    "Shopping",
    "Wellness",
    "Eco-Tourism",
    "Sports",
    "Cruises",
    "Art & Museums",
    "Mountains",
    "Water Sports",
    "Desert Safari",
    "Local Experiences",
    "Romantic",
    "Family-Friendly",
    "Adventure Sports",
    "Bird Watching",
    "Volunteering",
  ];


  const [selectedPlace, setSelectedPlace] = useState("");
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [suggestedPlaces, setSuggestedPlaces] = useState([]);
  const [visiblePlaces, setVisiblePlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const loaderRef = useRef(null);

  const toggleInterest = (interest) => {
    setSelectedInterests((prev) =>
      prev.includes(interest)
        ? prev.filter((i) => i !== interest)
        : [...prev, interest]
    );
  };

  // Fetch suggestions
  const handleSuggestPlaces = async () => {
    if (!selectedPlace || selectedInterests.length === 0) {
      alert("Please select a place and at least one interest");
      return;
    }

    setLoading(true);
    setSuggestedPlaces([]);
    setVisiblePlaces([]);
    setHasMore(true);

    try {
      const res = await fetch(`${API_URL}/suggest-places`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          place: selectedPlace,
          interests: selectedInterests.join(", "),
          limit: 20, // fetch up to 20 suggestions
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to fetch suggestions");

      setSuggestedPlaces(data.suggestions);
      setVisiblePlaces(data.suggestions.slice(0, 4)); // show first 4
    } catch (err) {
      console.error(err);
      alert("Failed to fetch suggestions. Try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Load more 4 cards
  const loadMore = () => {
    if (!hasMore) return;
    setLoading(true);

    setTimeout(() => { // simulate async load for smooth effect
      const currentLength = visiblePlaces.length;
      const nextPlaces = suggestedPlaces.slice(currentLength, currentLength + 4);
      setVisiblePlaces((prev) => [...prev, ...nextPlaces]);
      if (currentLength + nextPlaces.length >= suggestedPlaces.length) {
        setHasMore(false);
      }
      setLoading(false);
    }, 300);
  };

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMore();
        }
      },
      { threshold: 1 }
    );

    if (loaderRef.current) observer.observe(loaderRef.current);

    return () => {
      if (loaderRef.current) observer.unobserve(loaderRef.current);
    };
  }, [loaderRef, visiblePlaces, suggestedPlaces, hasMore, loading]);

  return (
    <div className="explore-container">
      <Navbar />
      <div className="explorer-search">
        <h1 className="explorer-title-search">Find Your Dream Vacation</h1>

        <div className="explorer-inputs-search">
          <input
            type="text"
            placeholder="Enter a place you want to explore"
            value={selectedPlace}
            onChange={(e) => setSelectedPlace(e.target.value)}
            className="explorer-place-search"
          />

          <div className="explorer-interests-search">
            {interestOptions.map((interest) => (
              <label key={interest}>
                <input
                  type="checkbox"
                  value={interest}
                  checked={selectedInterests.includes(interest)}
                  onChange={() => toggleInterest(interest)}
                />
                {interest}
              </label>
            ))}
          </div>

          <button
            onClick={handleSuggestPlaces}
            disabled={loading}
            className="explorer-suggest-btn-search"
          >
            {loading ? "Finding..." : "Suggest Places"}
          </button>
        </div>

        <div className="explorer-places-container-search">
          {visiblePlaces.map((place, idx) => (
            <div key={idx} className="explorer-card-search">
              <h3>{place.destination}</h3>
              <p>{place.description}</p>
              <button
                onClick={() => navigate("/plan-trip")}
                className="explorer-plan-btn-search"
              >
                Plan this trip
              </button>
            </div>
          ))}
        </div>

        {/* Loader div triggers IntersectionObserver */}
        {hasMore && (
          <div
            ref={loaderRef}
            style={{
              height: "40px",
              margin: "20px 0",
              textAlign: "center",
              color: "#fff",
              fontWeight: "500",
            }}
          >
            {loading && "Loading more places..."}
          </div>
        )}

        {!hasMore && visiblePlaces.length > 0 && (
          <p
            style={{
              marginTop: "20px",
              color: "#fff",
              textAlign: "center",
              fontWeight: "500",
            }}
          >
            No more places to show
          </p>
        )}
      </div>
      <Footer />
    </div>
  );
}
